import { Trans } from '@lingui/react/macro';
import { type FC, type PropsWithChildren, type SVGProps } from 'react';

import { type HealthCenterPodType } from '@/api/healthCenter';
import BulletSeparated from '@/components/BulletSeparated';
import { LinkButton } from '@/components/buttons';
import Icon from '@/components/icon';
import InfoPopover from '@/components/InfoPopover';
import { SkeletonRectangle } from '@/components/loaders';
import { Text, Title } from '@/components/typography';
import { cn } from '@/lib/utils';
import { useMe } from '@/providers/User';

import HealthPodStatusTag from './HealthPodStatusTag';

type HealthPodProps = {
  isLoading: boolean;
  icon: FC<SVGProps<SVGSVGElement>>;
  title: string;
  titleInfoPopover?: {
    title: React.ReactNode;
    body: React.ReactNode;
  };
  instructions: string;
  link?: string;
  data?: HealthCenterPodType;
};

const HealthPod = ({
  isLoading,
  icon,
  title,
  titleInfoPopover,
  instructions,
  link,
  data,
  children,
}: PropsWithChildren<HealthPodProps>) => {
  const user = useMe();
  const isPaidUser = user?.isPaid;
  const { isAvailable, isComplete, isFree } = data ?? {};
  const notIncludedInPlan = !isPaidUser && !isFree;

  const disabled = notIncludedInPlan || !isAvailable || isLoading;

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-lg border border-gray-300 bg-white p-6',
        disabled && 'opacity-50',
      )}
    >
      <div className="flex items-center gap-2">
        <Icon size="large" icon={icon} />
        <Title level={3} weight="bold" className="flex-1">
          <div className="flex items-center gap-1">
            {title}
            {titleInfoPopover && (
              <InfoPopover title={titleInfoPopover.title} body={titleInfoPopover.body} />
            )}
          </div>
        </Title>
        {!isLoading && link && (
          <LinkButton to={link} disabled={disabled}>
            <Trans>Take Me There</Trans>
          </LinkButton>
        )}
      </div>
      <div className="flex flex-1 flex-col">
        <Text variant="footnote">{instructions}</Text>
        <Text variant="body2" className="flex flex-wrap gap-1 leading-5.5">
          {isLoading ? (
            <SkeletonRectangle width="50%" height={16} style={{ marginTop: 4 }} />
          ) : (
            <BulletSeparated>{children}</BulletSeparated>
          )}
        </Text>
      </div>
      <div className="pt-2">
        {isLoading ? (
          <SkeletonRectangle width={95} height={29} />
        ) : (
          <HealthPodStatusTag
            isAvailable={isAvailable}
            notIncludedInPlan={notIncludedInPlan}
            isComplete={isComplete}
            disabled={disabled}
            link={link}
          />
        )}
      </div>
    </div>
  );
};

export default HealthPod;
